import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import googleAdsService from '../../services/api/googleAdsService';
import { GoogleAdsConnection, GoogleAdsPending } from '../../types/googleAds';
import { useConfirm } from '../tasky/modal';
import { GOOGLE_ADS_STRINGS, reviewSafeText, useMetaLang } from '../../i18n/metaReviewLang';

const CARD = 'bg-white border border-gray-200 rounded-xl p-6 mb-5';

const HEADER = 'flex justify-between gap-4 items-start mb-[18px]';

const PROVIDER = 'flex items-center gap-3';

const LOGO = 'w-10 h-10 rounded-[10px] text-white font-extrabold grid place-items-center bg-[image:conic-gradient(from_215deg,#4285f4,#34a853,#fbbc04,#ea4335,#4285f4)]';

const TITLE = 'text-[16px] font-bold text-gray-800';

const META = 'text-[12px] text-gray-500';

const BADGE_BASE = 'px-[10px] py-1 rounded-full text-[12px] font-bold';
const BADGE_OK = 'text-green-800 bg-green-100';
const BADGE_NG = 'text-gray-500 bg-gray-100';

const ACCOUNT = 'border border-gray-200 rounded-[10px] px-[14px] py-3 mt-[10px] flex justify-between gap-3 items-center';

const ACCOUNT_NAME = 'text-gray-900 text-[14px] font-bold';

const ACCOUNT_META = 'text-gray-500 text-[12px] mt-[3px]';

const ACTIONS = 'flex gap-2 mt-4 flex-wrap';

const BUTTON_BASE = 'border rounded-lg px-[14px] py-[9px] text-[13px] font-semibold cursor-pointer disabled:opacity-[0.55] disabled:cursor-not-allowed';
const BUTTON_DEFAULT = `${BUTTON_BASE} border-gray-300 bg-white text-gray-700`;
const BUTTON_PRIMARY = `${BUTTON_BASE} border-[#1a73e8] bg-[#1a73e8] text-white`;
const BUTTON_DANGER = `${BUTTON_BASE} border-red-200 bg-white text-red-600`;

const ALERT_BASE = 'mt-[14px] border rounded-lg px-3 py-[10px] text-[13px]';
const ALERT_INFO = `${ALERT_BASE} border-green-200 bg-green-50 text-green-800`;
const ALERT_ERROR = `${ALERT_BASE} border-red-200 bg-red-50 text-red-700`;

const CANDIDATE_LIST = 'mt-[14px] grid gap-2';

const CANDIDATE = 'flex items-start gap-[10px] border border-blue-100 rounded-[9px] px-3 py-[11px] cursor-pointer bg-[#f8fbff]';

function errorMessage(error: unknown, fallback: string): string {
  const typed = error as { response?: { data?: { message?: string } } };
  return typed.response?.data?.message || fallback;
}

const GoogleAdsConnectionCard: React.FC = () => {
  const [lang] = useMetaLang();
  const t = GOOGLE_ADS_STRINGS[lang];
  const confirmDialog = useConfirm();
  const navigate = useNavigate();
  const [connection, setConnection] = useState<GoogleAdsConnection | null>(null);
  const [pending, setPending] = useState<GoogleAdsPending | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [clock, setClock] = useState(Date.now());
  const [restartRequired, setRestartRequired] = useState(false);
  const operationInFlight = useRef(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setConnection(await googleAdsService.getConnection());
    } catch (err) {
      setError(lang === 'en' ? t.loadError : errorMessage(err, t.loadError));
    } finally {
      setLoading(false);
    }
  }, [lang, t.loadError]);

  useEffect(() => {
    load();
  }, [load]);

  const restorePending = useCallback(async (legacyState?: string) => {
    if (operationInFlight.current) return;
    operationInFlight.current = true;
    setSubmitting(true);
    try {
      const restored = await googleAdsService.getPending(legacyState);
      setPending(restored);
      setClock(Date.now());
      setSelected((current) => {
        const available = restored?.candidates.filter((item) => !item.manager) || [];
        const retained = current.filter((id) => available.some((item) => item.customer_id === id));
        return retained.length ? retained : available.slice(0, 1).map((item) => item.customer_id);
      });
      if (restored) {
        setError(null);
        setRestartRequired(false);
      }
      // New callbacks carry no state in the URL. Remove legacy parameters
      // after loading, while preserving unrelated media-provider parameters.
      const url = new URL(window.location.href);
      if (url.searchParams.get('google_oauth') === 'pending') {
        url.searchParams.delete('google_oauth');
        url.searchParams.delete('state');
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      }
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 410 || status === 404) {
        setPending(null);
        setRestartRequired(true);
      }
      // Members may view connection metadata but cannot resume a leader's grant.
      if (status !== 403) {
        setError(lang === 'en' ? t.resumeError : errorMessage(err, t.resumeError));
      }
    } finally {
      operationInFlight.current = false;
      setSubmitting(false);
    }
  }, [lang, t.resumeError]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const oauth = query.get('google_oauth');
    if (oauth === 'error') {
      const reason = query.get('reason') || 'oauth_error';
      setError(t.oauthErrors[reason] || t.oauthError);
      setRestartRequired(true);
    } else if (oauth === 'completed') {
      setSuccess(t.connectedSuccess);
    }
    restorePending(oauth === 'pending' ? query.get('state') || undefined : undefined);
  }, [restorePending, t]);

  useEffect(() => {
    if (!pending) return;
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [pending]);

  const remainingSeconds = pending ? Math.max(0, Math.ceil((Date.parse(pending.expires_at) - clock) / 1000)) : 0;
  const expired = !!pending && remainingSeconds === 0;
  const candidates = pending?.candidates || [];

  const connect = async () => {
    if (operationInFlight.current) return;
    operationInFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      window.location.assign(await googleAdsService.initiateOAuth());
    } catch (err) {
      setError(lang === 'en' ? t.oauthStartError : errorMessage(err, t.oauthStartError));
      operationInFlight.current = false;
      setSubmitting(false);
    }
  };

  const confirm = async () => {
    if (operationInFlight.current) return;
    if (!pending || expired || selected.length === 0) {
      setError(t.selectRequired);
      return;
    }
    operationInFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await googleAdsService.confirmAccounts(pending.state, selected);
      setPending(null);
      setSelected([]);
      setRestartRequired(false);
      setSuccess(t.savedSuccess);
      window.history.replaceState({}, '', window.location.pathname);
      await load();
    } catch (err) {
      setError(lang === 'en' ? t.saveError : errorMessage(err, t.saveError));
    } finally {
      operationInFlight.current = false;
      setSubmitting(false);
    }
  };

  const disconnect = async (id: number) => {
    if (operationInFlight.current) return;
    if (!(await confirmDialog({ title: t.confirmDisconnect, tone: 'destructive', confirmText: t.disconnectConfirm }))) return;
    operationInFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await googleAdsService.disconnect(id);
      setSuccess(t.disconnectedSuccess);
      await load();
    } catch (err) {
      setError(lang === 'en' ? t.disconnectError : errorMessage(err, t.disconnectError));
    } finally {
      operationInFlight.current = false;
      setSubmitting(false);
    }
  };

  const activeAccounts = useMemo(
    () => connection?.accounts.filter((account) => account.status !== 'disconnected') || [],
    [connection],
  );

  return (
    <section className={CARD} data-testid="google-ads-connection-card" data-tutorial="media-connect-google">
      <div className={HEADER}>
        <div className={PROVIDER}>
          <div className={LOGO}>G</div>
          <div>
            <div className={TITLE}>Google Ads</div>
            <div className={META}>Google Ads API {connection?.api_version || 'v22'}{t.apiMeta}</div>
          </div>
        </div>
        <span className={`${BADGE_BASE} ${connection?.connected ? BADGE_OK : BADGE_NG}`}>
          {connection?.connected ? t.connected : t.notConnected}
        </span>
      </div>

      {loading && <div className={META}>{t.loading}</div>}

      {activeAccounts.map((account) => (
        <div className={ACCOUNT} key={account.id}>
          <div>
            <div className={ACCOUNT_NAME}>{reviewSafeText(account.account_name, lang, 'Google Ads account')}</div>
            <div className={ACCOUNT_META}>
              Customer ID •••• {account.customer_id_suffix}
              {account.currency_code ? ` / ${account.currency_code}` : ''}
              {account.time_zone ? ` / ${account.time_zone}` : ''}
              {account.is_test_account ? ` / ${t.testAccount}` : ''}
            </div>
            <div className={ACCOUNT_META}>
              {t.status}: {account.status}
              {account.last_synced_at ? ` / ${t.lastSync} ${new Date(account.last_synced_at).toLocaleString(lang === 'ja' ? 'ja-JP' : 'en-US')}` : ''}
            </div>
          </div>
          <button className={BUTTON_DANGER} onClick={() => disconnect(account.id)} disabled={submitting}>{t.disconnect}</button>
        </div>
      ))}

      {candidates.length > 0 && (
        <>
          <div className={ALERT_INFO}>{t.mccNotice}</div>
          {pending && <div className={ACCOUNT_META}>{t.selectionExpires}: {new Date(pending.expires_at).toLocaleString(lang === 'ja' ? 'ja-JP' : 'en-US')}{lang === 'ja' ? `（${t.remainingMinutes(Math.ceil(remainingSeconds / 60))}）` : ` (${t.remainingMinutes(Math.ceil(remainingSeconds / 60))})`}</div>}
          {expired && <div className={ALERT_ERROR}>{t.expired}</div>}
          <div className={CANDIDATE_LIST}>
            {candidates.map((candidate) => (
              <label className={CANDIDATE} key={candidate.customer_id}>
                <input
                  type="checkbox"
                  disabled={candidate.manager || submitting || expired}
                  checked={!candidate.manager && selected.includes(candidate.customer_id)}
                  onChange={(event) => setSelected((current) => event.target.checked
                    ? [...current, candidate.customer_id]
                    : current.filter((id) => id !== candidate.customer_id))}
                />
                <span>
                  <div className={ACCOUNT_NAME}>{reviewSafeText(candidate.account_name, lang, 'Google Ads account')}</div>
                  <div className={ACCOUNT_META}>
                    •••• {candidate.customer_id.slice(-4)} / {candidate.currency_code || t.currencyUnset}
                    {candidate.manager ? ' / MCC' : ''}
                    {candidate.test_account ? ` / ${t.test}` : ''}
                  </div>
                </span>
              </label>
            ))}
          </div>
          <div className={ACTIONS}>
            <button className={BUTTON_PRIMARY} onClick={confirm} disabled={submitting || expired || selected.length === 0}>
              {t.connectSelected}
            </button>
          </div>
        </>
      )}

      {error && <div className={ALERT_ERROR}>{error}</div>}
      {success && <div className={ALERT_INFO}>{success}</div>}

      <p className={META}>
        {t.privacyBody}{lang === 'ja' ? '' : ' '}
        <a
          href="https://lp.tasky-ai.com/legal/privacy.html#google-ads"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-gray-600"
        >
          {t.privacyPolicy}
        </a>
      </p>

      <div className={ACTIONS}>
        <button className={BUTTON_DEFAULT} onClick={() => restorePending()} disabled={submitting || expired}>{t.resume}</button>
        <button className={BUTTON_PRIMARY} onClick={connect} disabled={submitting}>
          {pending || restartRequired ? t.restart : connection?.connected ? t.reconnect : t.connectOAuth}
        </button>
        {connection?.connected && (
          <button className={BUTTON_DEFAULT} onClick={() => navigate('/ad-dashboard/google')}>{t.dashboard}</button>
        )}
      </div>
    </section>
  );
};

export default GoogleAdsConnectionCard;
